import { createRoot } from "react-dom/client";
import { Component, type ReactNode, type ErrorInfo } from "react";
import { TolgeeProvider } from "@tolgee/react";
import { tolgee } from "./i18n/tolgee";
import { InstanceProvider } from "./contexts/InstanceContext";
import App from "./App.tsx";
import "./index.css";

// Auto-recover from stale chunk references after a deploy: when Vite tries to
// load a hashed chunk that no longer matches the freshly built index.html,
// it fires `vite:preloadError`. Force one cache-busting reload (guarded by
// sessionStorage so we never loop) so the user lands on the new build instead
// of the "App crashed / Failed to fetch dynamically imported module" screen.
window.addEventListener("vite:preloadError", () => {
  if (sessionStorage.getItem("postcash_reload_after_preload_error")) return;
  sessionStorage.setItem("postcash_reload_after_preload_error", "1");
  window.location.reload();
});
window.addEventListener("load", () => {
  sessionStorage.removeItem("postcash_reload_after_preload_error");
});

class GlobalErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[GlobalErrorBoundary]", error, info.componentStack);
    // Stale chunk after deploy → Vite's lazy import rejects with this exact message.
    // Same auto-recover dance as the vite:preloadError listener.
    if (/Failed to fetch dynamically imported module/i.test(error.message)) {
      if (!sessionStorage.getItem("postcash_reload_after_preload_error")) {
        sessionStorage.setItem("postcash_reload_after_preload_error", "1");
        window.location.reload();
      }
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, color: "#ef4444", fontFamily: "monospace", fontSize: 14 }}>
          <h2 style={{ margin: "0 0 12px" }}>App crashed</h2>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById("root")!).render(
  <GlobalErrorBoundary>
    <TolgeeProvider tolgee={tolgee} fallback={null}>
      <InstanceProvider>
        <App />
      </InstanceProvider>
    </TolgeeProvider>
  </GlobalErrorBoundary>
);
